// src/components/pipelines/sheets/RuleBuilder.jsx
import React, { useState } from 'react';
import { Button } from '../../ui/button.tsx';
import { Input } from '../../ui/input.tsx';
import { Label } from '../../ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select.tsx';
import { Card } from '../../ui/card.tsx';

/**
 * RuleBuilder - Form for creating advance rule configurations
 *
 * Returns rule data for creating AutomationRule with trigger_type 'time_based', 'conditional', or 'system_event'
 */
const RuleBuilder = ({ stage, pipelineStages, onSave, onCancel, initialData = null }) => {
  const [ruleType, setRuleType] = useState(initialData?.trigger_type || '');
  const [ruleName, setRuleName] = useState(initialData?.name || '');
  const [nextStageId, setNextStageId] = useState(initialData?.next_stage_id || '');

  // Time-based fields
  const [durationValue, setDurationValue] = useState('');
  const [durationUnit, setDurationUnit] = useState('days');

  // Event-based fields
  const [eventType, setEventType] = useState(initialData?.trigger_event || '');

  // Conditional fields
  const [conditionField, setConditionField] = useState('');
  const [conditionOperator, setConditionOperator] = useState('>=');
  const [conditionValue, setConditionValue] = useState('');

  // Get next stage in sequence
  const currentStagePosition = stage.position;
  const nextStageInSequence = pipelineStages.find(s => s.position === currentStagePosition + 1);

  const handleSave = () => {
    if (!ruleType) {
      alert('Please select a rule type');
      return;
    }

    const ruleData = {
      name: ruleName || `Auto-advance rule`,
      trigger_type: ruleType,
      trigger_event: null,
      conditions: [],
      actions: [],
      next_stage_id: nextStageId || (nextStageInSequence?.id || null),
      priority: 0,
      is_active: true
    };

    // Add type-specific configuration
    switch (ruleType) {
      case 'time_based':
        if (!durationValue || durationValue <= 0) {
          alert('Please enter a valid duration');
          return;
        }

        // Convert duration to days for the condition
        let daysValue = parseInt(durationValue);
        if (durationUnit === 'hours') {
          daysValue = daysValue / 24;
        } else if (durationUnit === 'weeks') {
          daysValue = daysValue * 7;
        }

        ruleData.conditions = [{
          field: 'days_in_stage',
          operator: '>=',
          value: daysValue
        }];
        ruleData.name = ruleName || `Advance after ${durationValue} ${durationUnit}`;
        break;

      case 'system_event':
        if (!eventType) {
          alert('Please select an event type');
          return;
        }
        ruleData.trigger_event = eventType;
        ruleData.name = ruleName || `Advance on ${eventType.replace(/_/g, ' ')}`;
        break;

      case 'conditional':
        if (!conditionField || !conditionValue) {
          alert('Please fill in all condition fields');
          return;
        }
        ruleData.conditions = [{
          field: conditionField,
          operator: conditionOperator,
          value: conditionValue
        }];
        ruleData.name = ruleName || `Advance when ${conditionField} ${conditionOperator} ${conditionValue}`;
        break;

      default:
        alert('Invalid rule type');
        return;
    }

    onSave(ruleData);
  };

  return (
    <Card className="p-4 bg-slate-800/70 border-slate-700">
      <div className="space-y-4">
        {/* Rule Name */}
        <div>
          <Label htmlFor="rule-name" className="text-sm text-slate-300">
            Rule Name (optional - auto-generated if blank)
          </Label>
          <Input
            id="rule-name"
            value={ruleName}
            onChange={(e) => setRuleName(e.target.value)}
            placeholder="e.g., Auto-advance after 3 days"
            className="mt-1 bg-slate-900 border-slate-700 text-slate-200"
          />
        </div>

        {/* Rule Type */}
        <div>
          <Label htmlFor="rule-type" className="text-sm text-slate-300">
            Rule Type <span className="text-red-400">*</span>
          </Label>
          <Select value={ruleType} onValueChange={setRuleType}>
            <SelectTrigger className="mt-1 bg-slate-900 border-slate-700 text-slate-200">
              <SelectValue placeholder="Select rule type" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="time_based" className="text-slate-200">
                Time-based (after X time in stage)
              </SelectItem>
              <SelectItem value="system_event" className="text-slate-200">
                Event-based (when event occurs)
              </SelectItem>
              <SelectItem value="conditional" className="text-slate-200">
                Conditional (when condition is true)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Type-Specific Fields */}
        {ruleType === 'time_based' && (
          <div className="space-y-3">
            <div>
              <Label className="text-sm text-slate-300">
                Advance After <span className="text-red-400">*</span>
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="number"
                  value={durationValue}
                  onChange={(e) => setDurationValue(e.target.value)}
                  placeholder="Enter number"
                  className="flex-1 bg-slate-900 border-slate-700 text-slate-200"
                  min="1"
                />
                <Select value={durationUnit} onValueChange={setDurationUnit}>
                  <SelectTrigger className="w-32 bg-slate-900 border-slate-700 text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="hours" className="text-slate-200">Hours</SelectItem>
                    <SelectItem value="days" className="text-slate-200">Days</SelectItem>
                    <SelectItem value="weeks" className="text-slate-200">Weeks</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {ruleType === 'system_event' && (
          <div>
            <Label htmlFor="event-type" className="text-sm text-slate-300">
              Event Type <span className="text-red-400">*</span>
            </Label>
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger className="mt-1 bg-slate-900 border-slate-700 text-slate-200">
                <SelectValue placeholder="Select event" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="invoice_paid" className="text-slate-200">Invoice Paid</SelectItem>
                <SelectItem value="appointment_completed" className="text-slate-200">Appointment Completed</SelectItem>
                <SelectItem value="form_submitted" className="text-slate-200">Form Submitted</SelectItem>
                <SelectItem value="email_opened" className="text-slate-200">Email Opened</SelectItem>
                <SelectItem value="email_clicked" className="text-slate-200">Email Link Clicked</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {ruleType === 'conditional' && (
          <div className="space-y-3">
            <Label className="text-sm text-slate-300">
              Condition <span className="text-red-400">*</span>
            </Label>
            <div className="flex gap-2">
              <Input
                value={conditionField}
                onChange={(e) => setConditionField(e.target.value)}
                placeholder="Field (e.g., customer.total_spent)"
                className="flex-1 bg-slate-900 border-slate-700 text-slate-200"
              />
              <Select value={conditionOperator} onValueChange={setConditionOperator}>
                <SelectTrigger className="w-24 bg-slate-900 border-slate-700 text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="==" className="text-slate-200">==</SelectItem>
                  <SelectItem value="!=" className="text-slate-200">!=</SelectItem>
                  <SelectItem value=">" className="text-slate-200">&gt;</SelectItem>
                  <SelectItem value="<" className="text-slate-200">&lt;</SelectItem>
                  <SelectItem value=">=" className="text-slate-200">&gt;=</SelectItem>
                  <SelectItem value="<=" className="text-slate-200">&lt;=</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={conditionValue}
                onChange={(e) => setConditionValue(e.target.value)}
                placeholder="Value"
                className="flex-1 bg-slate-900 border-slate-700 text-slate-200"
              />
            </div>
            <p className="text-xs text-slate-500">
              Example: days_in_stage &gt;= 7 or customer.is_vip == true
            </p>
          </div>
        )}

        {/* Next Stage Selection */}
        <div>
          <Label htmlFor="next-stage" className="text-sm text-slate-300">
            Move To Stage
          </Label>
          <Select value={nextStageId} onValueChange={setNextStageId}>
            <SelectTrigger className="mt-1 bg-slate-900 border-slate-700 text-slate-200">
              <SelectValue placeholder={nextStageInSequence ? `Next stage: ${nextStageInSequence.name}` : 'Select stage'} />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {pipelineStages
                .filter(s => s.id !== stage.id) // Don't allow moving to same stage
                .sort((a, b) => a.position - b.position)
                .map(s => (
                  <SelectItem key={s.id} value={s.id} className="text-slate-200">
                    {s.name} {s.id === nextStageInSequence?.id && '(Next)'}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-1">
            {nextStageInSequence
              ? `Default: "${nextStageInSequence.name}" (next in sequence)`
              : 'No next stage in sequence - please select one'}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            Save Rule
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default RuleBuilder;
